/**
 * OrthogonalSliceViewport2D - Axial, sagittal, and coronal slice viewports.
 *
 * Configures camera orientation and slice plane for standard orthogonal slicing
 * directions based on ViewportType (SLICE_AXIAL, SLICE_SAGITTAL, SLICE_CORONAL).
 */

import { SliceViewport2D } from './slice-viewport-2d';
import { ViewportType } from './types';
import type { Volume } from '../domain/volume';

export class OrthogonalSliceViewport2D extends SliceViewport2D {
    protected configureSliceOrientation(volume: Volume): void {
        const center = volume.center;
        const bounds = volume.bounds;
        const camera = this.getRenderer(0).getActiveCamera();
        let sliceWidth: number;
        let sliceHeight: number;

        switch (this.type) {
            case ViewportType.SLICE_AXIAL:
                // Axial: camera looks from inferior (feet→head), radiological convention
                this.slicePlane.setOrigin(center[0], center[1], center[2]);
                this.slicePlane.setNormal(0, 0, 1);
                camera.setPosition(center[0], center[1], center[2] - 500);
                camera.setFocalPoint(center[0], center[1], center[2]);
                camera.setViewUp(0, -1, 0);
                sliceWidth = bounds[1] - bounds[0]; // X range
                sliceHeight = bounds[3] - bounds[2]; // Y range
                break;

            case ViewportType.SLICE_SAGITTAL:
                // Sagittal: slice perpendicular to X (left-right)
                this.slicePlane.setOrigin(center[0], center[1], center[2]);
                this.slicePlane.setNormal(1, 0, 0);
                camera.setPosition(center[0] + 500, center[1], center[2]);
                camera.setFocalPoint(center[0], center[1], center[2]);
                camera.setViewUp(0, 0, 1);
                sliceWidth = bounds[3] - bounds[2]; // Y range
                sliceHeight = bounds[5] - bounds[4]; // Z range
                break;

            case ViewportType.SLICE_CORONAL:
                // Coronal: camera looks from anterior (front→back), radiological convention
                this.slicePlane.setOrigin(center[0], center[1], center[2]);
                this.slicePlane.setNormal(0, 1, 0);
                camera.setPosition(center[0], center[1] - 500, center[2]);
                camera.setFocalPoint(center[0], center[1], center[2]);
                camera.setViewUp(0, 0, 1);
                sliceWidth = bounds[1] - bounds[0]; // X range
                sliceHeight = bounds[5] - bounds[4]; // Z range
                break;

            default:
                return;
        }

        // Reference and initial framing coincide here: both fit the volume extent
        this.setZoomReference(sliceWidth, sliceHeight);
        this.fitToViewport(sliceWidth, sliceHeight);
    }
}
